import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, INJECTOR } from '@angular/core';
import { setMetadata, getMetadata } from '@rxap/reflect-metadata';
import { RxapError, GenerateRandomString, deepMerge } from '@rxap/utilities';
import { Subject } from 'rxjs';

var DefinitionMetadataKeys;
(function (DefinitionMetadataKeys) {
    DefinitionMetadataKeys["META_DATA"] = "rxap/definition/metadata";
    DefinitionMetadataKeys["PACKAGE_NAME"] = "rxap/definition/package-name";
    DefinitionMetadataKeys["CLASS_NAME"] = "rxap/definition/class-name";
})(DefinitionMetadataKeys || (DefinitionMetadataKeys = {}));
function DefinitionMetadata(metadataOrId, className, packageName) {
    return function (target) {
        setMetadata(DefinitionMetadataKeys.META_DATA, typeof metadataOrId === 'string' ? { id: metadataOrId } : metadataOrId, target);
        setMetadata(DefinitionMetadataKeys.PACKAGE_NAME, packageName, target);
        setMetadata(DefinitionMetadataKeys.CLASS_NAME, className, target);
    };
}
// eslint-disable-next-line @typescript-eslint/ban-types
function GetDefinitionMetadata(definition) {
    if (typeof definition === 'function') {
        return getMetadata(DefinitionMetadataKeys.META_DATA, definition);
    }
    else {
        return getMetadata(DefinitionMetadataKeys.META_DATA, definition.constructor);
    }
}
// eslint-disable-next-line @typescript-eslint/ban-types
function GetDefinitionClassName(definition) {
    if (typeof definition === 'function') {
        return getMetadata(DefinitionMetadataKeys.CLASS_NAME, definition);
    }
    else {
        return getMetadata(DefinitionMetadataKeys.CLASS_NAME, definition.constructor);
    }
}
// eslint-disable-next-line @typescript-eslint/ban-types
function GetDefinitionPackageName(definition) {
    if (typeof definition === 'function') {
        return getMetadata(DefinitionMetadataKeys.PACKAGE_NAME, definition);
    }
    else {
        return getMetadata(DefinitionMetadataKeys.PACKAGE_NAME, definition.constructor);
    }
}

const RXAP_DEFINITION_METADATA = new InjectionToken('rxap/definition/metadata');

class RxapDefinitionError extends RxapError {
    constructor(message, code, className) {
        super('@rxap/definition', message, code, className);
    }
}

class BaseDefinition {
    /**
     * A map of active Definition instances
     */
    static { this.instances = new Map(); }
    /**
     * Emits when a new Definition instance is initialised
     */
    static { this.initialised$ = new Subject(); }
    /**
     * Emits when a new Definition instance is initialised
     */
    static { this.destroyed$ = new Subject(); }
    constructor(metadata = null) {
        /**
         * Emits if the data source is destroyed
         */
        this.destroyed$ = new Subject();
        /**
         * Emits if the data source is initialised
         */
        this.initialised$ = new Subject();
        this.interceptors = new Set();
        /**
         * unique internal id
         *
         * @internal
         */
        this.__id = GenerateRandomString();
        this._initialised = false;
        if (metadata) {
            const decoratorMetadata = this.getMetadata();
            if (decoratorMetadata) {
                metadata = deepMerge(decoratorMetadata, metadata);
            }
        }
        else {
            metadata = this.getMetadata();
        }
        if (!metadata) {
            throw new RxapDefinitionError('Definition metadata is not defined', '', this.constructor.name);
        }
        this.metadata = metadata;
    }
    get id() {
        return this.metadata.id;
    }
    /**
     * @param definition
     */
    static add(definition) {
        this.instances?.set(definition.__id, definition);
        this.initialised$?.next(definition);
    }
    /**
     * @param definition
     */
    static remove(definition) {
        this.instances?.delete(definition.__id);
        this.destroyed$?.next(definition);
    }
    getMetadata() {
        return GetDefinitionMetadata(this.constructor);
    }
    applyMetadata(partialMetadata) {
        this.metadata = deepMerge(this.metadata, partialMetadata);
    }
    /**
     * @deprecated use ngOnDestroy instead
     */
    destroy() {
        this.ngOnDestroy();
    }
    ngOnDestroy() {
        this.destroyed$?.next();
        BaseDefinition.remove(this);
    }
    init() {
        if (this._initialised) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        if (typeof self['ngOnInit'] === 'function') {
            self.ngOnInit();
        }
        this._initialised = true;
        this.initialised$?.next();
        BaseDefinition.add(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: BaseDefinition, deps: [{ token: RXAP_DEFINITION_METADATA, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: BaseDefinition }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: BaseDefinition, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_DEFINITION_METADATA]
                }] }] });

class DefinitionLoader {
    constructor(injector) {
        this.injector = injector;
    }
    load(definitionIdOrInstanceOrInjectionToken, metadata, injector = this.injector, notFoundValue, flags) {
        if (typeof definitionIdOrInstanceOrInjectionToken === 'string') {
            throw new RxapDefinitionError('Creating a definition instance from the definition id is not supported by the DefinitionLoader service', '');
        }
        if (definitionIdOrInstanceOrInjectionToken instanceof BaseDefinition) {
            if (metadata) {
                definitionIdOrInstanceOrInjectionToken.applyMetadata(metadata);
            }
            return definitionIdOrInstanceOrInjectionToken;
        }
        return injector.get(definitionIdOrInstanceOrInjectionToken, notFoundValue, flags);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DefinitionLoader, deps: [{ token: INJECTOR }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DefinitionLoader, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DefinitionLoader, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [INJECTOR]
                }] }] });

// region 
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { BaseDefinition, DefinitionLoader, DefinitionMetadata, DefinitionMetadataKeys, GetDefinitionClassName, GetDefinitionMetadata, GetDefinitionPackageName, RXAP_DEFINITION_METADATA, RxapDefinitionError };
